// React
import { Fragment, useState } from "react"

// Third-Party Library
import moment from "moment"

// API
import { useQuery } from "react-query"
import { AtributApi } from "api"

// Components
import { Card, CardBody, InputGroup, Table, Input as ReactInput, Form, Row, Col } from "reactstrap"
import { ActionButton, Checkbox, FetchingStatus, Input } from "components"

// Form
import { Formik } from "formik"
import * as yup from "yup"

// Icons
import "./assets/style.css"
import { toast, validation } from "functions"
import { Api } from "./api"

const PaymentSection = ({
  state,
  children,
  totalPrice,
  tax
}) => {
  // Variables
  const cardStyle = {
    margin: "2rem auto",
    maxWidth: "48.125rem",
  }
  const noLink = "#"

  // API
  const getDataCurrency = useQuery(['dayPasses-payment-currency'], AtributApi.currency_rate)
  const getDataPayments = useQuery(['dayPasses-payment-paymentMethod'], AtributApi.payment_methods)

  const Td = ({ children, className, ...props }) => (
    <td className={`align-middle ${className}`} {...props}>{children}</td>
  )

  const ConversionSection = ({ price, tax }) => {
    // States
    const [dataConversion, setDataConversion] = useState({
      value: getDataCurrency.data[0].value,
      data: getDataCurrency.data[0]
    })

    const handleConversion = (e) => {
      const value = e.target.value
      const data = getDataCurrency.data.find(val => String(val.value) === String(value))

      setDataConversion({ value, data })
    }

    const convertedPrice = data => {
      return (price + tax) / 1000 * parseFloat(data.rate)
    }

    const dataConvertedDollar = getDataCurrency.data.find(val => val.label === "USD")

    return (
      <>
        <Td>
          <div className="d-flex justify-content-start align-items-center">
            <div className="fw-bold me-2">Currency Converter</div>
            <Input noMargin noLabel type="select" value={dataConversion.value} style={{ width: '100px' }} onChange={handleConversion}>
              {getDataCurrency.data.map((val, index) => <option key={index} value={val.value} className="me-2">{val.label}</option>)}
            </Input>
          </div>
          <small>Currency conversion rates are estimates only and subject to change based on your financial institution and payment gateway conversion rates. This is an example only, you will be charged based on the financial gateway provider currency.</small>
        </Td>
        <Td className="text-end">
          <div className="fw-bold">{dataConversion.data.label} {convertedPrice(dataConversion.data).toLocaleString('id-ID')}</div>
          <small>(estimate {dataConvertedDollar.label} {convertedPrice(dataConvertedDollar).toLocaleString('id-ID')})</small>
        </Td>
      </>
    )
  }

  const TdEnd = ({ children }) => (
    <div className="fw-bold text-end">{children}</div>
  )

  return (
    <div className="container">
      <h4 className="text-center fw-bolder">ORDER REVIEW</h4>

      <Formik
        enableReinitialize
        initialValues={{
          payment_method: "",
          terms: false,
          policy: false,
          ...state
        }}
        validationSchema={yup.object().shape({
          payment_method: yup.string().required("Select Payment Method"),
          terms: yup.boolean()
            .required(validation().required.label)
            .oneOf(validation().mustAccept.value, validation().mustAccept.label),
          policy: yup.boolean()
            .required(validation().required.label)
            .oneOf(validation().mustAccept.value, validation().mustAccept.label)
        })}
        onSubmit={(values, { setSubmitting }) => {
          Api.store(values).then(res => {
            const data = res.data
            window.location.href = data.link
          }).catch(err => {
            toast("error", "Error", err.response.statusText)
          }).finally(() => setSubmitting())
        }}
      >
        {({ handleSubmit, values, errors, touched, setFieldValue, isSubmitting }) => {
          return (
            <Fragment>
              <Card className="my-3 rounded-3 shadow-lg" style={cardStyle}>
                <CardBody>
                  <section>
                    <h4 className="text-center fw-bolder mb-1">Booking Details</h4>
                    <h5 className="text-center fw-bolder">Reservation date: {values.booking_date ? moment(values.booking_date).format("dddd, D MMMM YYYY") : "-"}</h5>
                  </section>

                  <Table responsive>
                    <thead>
                      <tr className="bg-light">
                        <th>Bed Details</th>
                        <th style={{ width: '300px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {children}
                      <tr>
                        <Td>
                          <div className="fw-bold">Sub Total</div>
                        </Td>
                        <Td>
                          <TdEnd>IDR {totalPrice.toLocaleString('id-ID')}</TdEnd>
                        </Td>
                      </tr>
                      <tr>
                        <Td>
                          <div className="fw-bold">Promo Code</div>
                        </Td>
                        <Td>
                          <div className="fw-bold">
                            <InputGroup>
                              <ReactInput />
                              <ActionButton color="primary" className="px-1 text-white">Submit</ActionButton>
                            </InputGroup>
                          </div>
                        </Td>
                      </tr>
                      <tr>
                        <Td>
                          <div className="fw-bold">10% Goverment Tax & 7% Service Charge</div>
                        </Td>
                        <Td>
                          <TdEnd>IDR {(totalPrice + (totalPrice * 0.17)).toLocaleString('id-ID')}</TdEnd>
                        </Td>
                      </tr>
                      <tr className="bg-light">
                        <Td>
                          <div className="fw-bold">Grand Total</div>
                        </Td>
                        <Td>
                          <TdEnd>IDR {(totalPrice + tax).toLocaleString('id-ID')}</TdEnd>
                        </Td>
                      </tr>
                      {getDataCurrency.isLoading || getDataCurrency.isError ? (
                        <tr>
                          <Td colSpan={2}>
                            <FetchingStatus loading={getDataCurrency.isLoading} />
                          </Td>
                        </tr>
                      ) : (
                        <tr>
                          <ConversionSection price={totalPrice} tax={tax} />
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>

              <Form onSubmit={handleSubmit} className="my-3">
                <Card className="rounded-3 shadow-lg" style={cardStyle}>
                  <CardBody>
                    <div className="container-md">
                      <h4 className="text-center fw-bolder mb-3">How would you like to pay?</h4>

                      {getDataPayments.isLoading || getDataPayments.isError ? (
                        <Td colSpan={2}>
                          <FetchingStatus loading={getDataPayments.isLoading} />
                        </Td>
                      ) : (
                        <Row className="justify-content-center align-items-stretch text-center mb-3">
                          {getDataPayments.data.map((val, index) => {
                            // Variables
                            const isSelected = Boolean(values.payment_method === val.name)
                            const image = val.image ?? "[]"

                            return (
                              <Col lg={4} md={6} key={index} className="d-flex align-items-stretch my-1">
                                <Card
                                  className={`border-${Boolean(errors.payment_method && touched.payment_method) ? 'danger' : isSelected ? 'primary' : 'secondary'} shadow-none w-100`}
                                  style={{ backgroundColor: isSelected && 'rgba(104, 204, 203, 0.1)', cursor: 'pointer' }}
                                  onClick={() => setFieldValue('payment_method', val.name)}
                                >
                                  <CardBody className="d-flex flex-column text-center">
                                    <div className="d-flex flex-wrap justify-content-center mb-2">
                                      {JSON.parse(image).map((val, index) => (
                                        <div>
                                          <img key={index} alt="Payment" style={{ border: '2px solid rgb(178, 190, 181)' }} src={`${process.env.REACT_APP_BASE_URL}${val}`} height="35" className="m-1" />
                                        </div>
                                      ))}
                                    </div>

                                    <h6 className="mt-auto fw-bolder">{val.display_name}</h6>
                                  </CardBody>
                                </Card>
                              </Col>
                            )
                          })}

                          {Boolean(errors.payment_method && touched.payment_method) && <small className="text-danger">{errors.payment_method}</small>}
                        </Row>
                      )}

                      <div className="mb-2">
                        <h5 className="fw-bolder">Guest Details</h5>

                        <Row>
                          <Col md={6}>
                            <Input label="First Name" value={values.first_name} readable />
                          </Col>

                          <Col md={6}>
                            <Input label="Last Name" value={values.last_name} readable />
                          </Col>

                          <Col md={6}>
                            <Input label="Phone" value={values.phone} readable />
                          </Col>

                          <Col md={6}>
                            <Input label="Zip Code" value={values.zip} readable />
                          </Col>

                          <Col md={6}>
                            <Input label="Nationality" value={values._nationality?.name ?? "-"} readable />
                          </Col>

                          <Col md={6}>
                            <Input label="Email" value={values.email} readable />
                          </Col>
                        </Row>

                        <Checkbox
                          onChange={() => setFieldValue('terms', !values.terms)}
                          value={values.terms}
                          errors={Boolean(errors.terms && touched.terms) && errors.terms}
                        >

                          I accept all Finns Bali Booking & venue  <a href={noLink} target="_blank" rel="noreferrer" className="text-info underline-hover">terms and conditions.</a>***
                        </Checkbox>
                        <Checkbox
                          onChange={() => setFieldValue('policy', !values.policy)}
                          value={values.policy}
                          errors={Boolean(errors.policy && touched.policy) && errors.policy}
                        >
                          I understand and accept the Finns Bali Booking face & head tattoo <a href={noLink} target="_blank" rel="noreferrer" className="text-info underline-hover">policy.</a>***
                        </Checkbox>

                        <div className="text-center mt-2">
                          <ActionButton
                            type="submit"
                            loading={isSubmitting}
                            color="primary"
                            className="text-white"
                            size="lg"
                          >
                            Pay Now
                          </ActionButton>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Form>
            </Fragment>
          )
        }}
      </Formik>
    </div>
  )
}

export { PaymentSection }